import { FC, useState, useEffect } from 'react'

import { useStore } from '@tanstack/react-store'
import { styled } from 'styled-components'

import { useHistory } from 'peach/router'

import * as rawAuthApi from 'core/auth/authApi'
import {
  authActions,
  useIsSessionActive,
  useAuthIsInitializing,
  getApiBaseFromEnvStore,
  authStore,
} from 'core/auth/authStore'
import { Clickable, FormLabel, Select, TextInput } from 'core/components'
import DialogComponent, {
  DialogActions,
} from 'core/components/lib/Dialog/Dialog'
import { FieldContainer, InputWrapper } from 'core/components/lib/Form'
import { R } from 'core/helpers'
import { useForm } from 'core/hooks'
import { validators } from 'core/hooks/useForm'
import { useActiveEnv } from 'core/stores/env'
import { emailValidationRegex } from 'core/types'

import EnvTable from '../env/EnvTable'
import Icon from 'core/components/Icon'
import { error, success } from 'core/styles/tokens'

type FormValues = {
  authMethod: string | null
  username: string
  password: string
  oneTimeCode: string
  apiKey: string
}

const LoginModal: FC = () => {
  const env = useActiveEnv()

  const [isManuallyClosed, setIsManuallyClosed] = useState(false)
  const [otcRequestStatus, setOtcRequestStatus] = useState<
    'idle' | 'pending' | 'success' | string
  >('idle')

  const loginModalExplicitlyRequested = useStore(
    authStore,
    (state) => state.loginModalExplicitlyRequested,
  )

  const currentEnvCompanyId = env?.companyId
  const currentEnvUserType = env?.userType

  const history = useHistory()

  const goToApp = () => history.push('/')

  const isSessionActive = useIsSessionActive()
  const isInitializing = useAuthIsInitializing()

  const shouldModalBeOpenGlobally = !isSessionActive && !isInitializing

  const effectiveIsOpen = shouldModalBeOpenGlobally && !isManuallyClosed

  useEffect(() => {
    if (shouldModalBeOpenGlobally) {
      setIsManuallyClosed(false)
    }
  }, [shouldModalBeOpenGlobally])

  useEffect(() => {
    if (loginModalExplicitlyRequested) {
      setIsManuallyClosed(false)
      authActions.setLoginModalExplicitlyRequested(false)
    }
  }, [loginModalExplicitlyRequested])

  const authTypeOptions = [
    { value: 'oneTimeCodeEmail', label: 'One Time Code Email' },
    { value: 'apiKey', label: 'API Key' },
    { value: 'basic', label: 'Basic' },
  ]

  const { fields, formState, handleSubmit } = useForm<FormValues>(
    {
      authMethod: 'oneTimeCodeEmail',
      username: '',
      password: '',
      oneTimeCode: '',
      apiKey: '',
    },
    {
      onSubmit: async ({
        authMethod,
        username,
        password,
        oneTimeCode,
        apiKey,
      }) => {
        if (authMethod === 'oneTimeCodeEmail') {
          await authActions.createSession({
            userType: 'agent',
            companyId: currentEnvCompanyId,
            username,
            password: oneTimeCode,
            authMethod,
          })
          goToApp()
        } else if (authMethod === 'apiKey') {
          await authActions.validateApiKey(apiKey)
          goToApp()
        } else if (authMethod === 'basic') {
          await authActions.createSession({
            companyId: currentEnvCompanyId,
            userType: currentEnvUserType,
            username,
            password,
            authMethod,
          })
          goToApp()
        } else {
          throw new Error(`Unknown Auth Method '${authMethod}'`)
        }
      },
      rules: {
        authMethod: validators.required,
        username: (value, { values }) =>
          values?.authMethod === 'basic' ||
          values?.authMethod === 'oneTimeCodeEmail'
            ? !R.isEmpty(value) && !value?.match(emailValidationRegex)
              ? 'Please provide a valid email address'
              : !value
              ? 'Required'
              : true
            : true,
        password: (value, { values }) =>
          values?.authMethod === 'basic' ? (!value ? 'Required' : true) : true,
        oneTimeCode: (value, { values }) =>
          values?.authMethod === 'oneTimeCodeEmail'
            ? !value
              ? 'Required'
              : true
            : true,
        apiKey: (value, { values }) =>
          values?.authMethod === 'apiKey' ? (!value ? 'Required' : true) : true,
      },
      validationType: 'onBlur',
    },
  )

  const dialogContent = (
    <>
      <EnvTable env={env} isActive />
      <form
        onKeyDown={(e) => {
          if (e.key === 'Enter' && formState?.isValid) {
            void handleSubmit()
          }
        }}
      >
        <Fields>
          <FieldContainer direction='column'>
            <FormLabel text='Auth Method' />
            <Select
              isSearchable={false}
              options={authTypeOptions}
              {...fields.authMethod}
            />
          </FieldContainer>

          {(fields.authMethod.value === 'basic' ||
            fields.authMethod.value === 'oneTimeCodeEmail') && (
            <FieldContainer direction='column'>
              <FormLabel text='Username' />
              <InputWrapper>
                <TextInput
                  placeholder='Enter username'
                  variant='outline'
                  {...fields.username}
                />
              </InputWrapper>
            </FieldContainer>
          )}

          {fields.authMethod.value === 'basic' && (
            <FieldContainer direction='column'>
              <FormLabel text='Password' />
              <TextInput
                placeholder='Enter password'
                type='password'
                variant='outline'
                {...fields.password}
              />
            </FieldContainer>
          )}

          {fields.authMethod.value === 'oneTimeCodeEmail' && (
            <FieldContainer direction='column'>
              <FormLabel text='One Time Code' />
              <OTCBox>
                <InputWrapper>
                  <TextInput
                    placeholder='Enter one time code'
                    variant='outline'
                    {...fields.oneTimeCode}
                  />
                </InputWrapper>
                <OTCButton
                  disabled={!fields.username.value || formState.isSubmitting}
                  onClick={async () => {
                    setOtcRequestStatus('pending')
                    const apiBase = getApiBaseFromEnvStore()
                    if (!apiBase) {
                      console.error(
                        'LoginModal: Cannot request OTC, apiBase not found.',
                      )
                      setOtcRequestStatus('error: apiBase not found')
                      return
                    }
                    try {
                      const resp = await rawAuthApi.requestOneTimeCode(
                        apiBase,
                        env?.companyId,
                        {
                          username: fields.username.value,
                          channel: 'email',
                          userType: 'agent',
                        },
                      )
                      if (resp?.status === 202) {
                        setOtcRequestStatus('success')
                      } else {
                        setOtcRequestStatus(
                          `error: status - ${resp?.status}; message - ${resp?.message}`,
                        )
                      }
                    } catch (e: any) {
                      setOtcRequestStatus(
                        `error: status - ${e?.status}; message - ${e?.message}`,
                      )
                    }
                  }}
                  variant='outline'
                >
                  {otcRequestStatus === 'idle'
                    ? 'Send Code'
                    : otcRequestStatus === 'pending'
                    ? 'Requesting...'
                    : otcRequestStatus === 'success'
                    ? 'Requested'
                    : 'Error'}
                  {(otcRequestStatus === 'success' ||
                    otcRequestStatus.startsWith('error:')) && (
                    <StyledOTCIcon
                      $status={otcRequestStatus}
                      color={otcRequestStatus === 'success' ? success : error}
                      margin='0 0 0 8px'
                      name={otcRequestStatus === 'success' ? 'check' : 'error'}
                      outlined
                    />
                  )}
                </OTCButton>
              </OTCBox>
              <OTCError>
                {otcRequestStatus.startsWith('error:') &&
                  (otcRequestStatus?.split(':')[1] ?? 'Unknown')}
              </OTCError>
            </FieldContainer>
          )}

          {fields.authMethod.value === 'apiKey' && (
            <FieldContainer direction='column'>
              <FormLabel text='API Key' />
              <TextInput
                placeholder='Enter API key'
                variant='outline'
                {...fields.apiKey}
              />
            </FieldContainer>
          )}
        </Fields>
        <DialogActions>
          <Clickable
            onClick={() => setIsManuallyClosed(true)}
            variant='secondary'
          >
            Close
          </Clickable>
          <Clickable
            disabled={!formState.isValid || formState.isSubmitting}
            isLoading={formState.isSubmitting}
            onClick={handleSubmit}
            variant='primary'
          >
            {fields.authMethod.value === 'apiKey' ? 'Validate' : 'Login'}
          </Clickable>
        </DialogActions>
      </form>
    </>
  )

  return (
    <DialogComponent
      content={dialogContent}
      isOpen={effectiveIsOpen}
      onOpenChange={(openState) => {
        if (!openState) {
          setIsManuallyClosed(true)
        }
      }}
      title={env?.label ? `Login to ${env.label}` : 'Login'}
      width='smallMedium'
    />
  )
}

export default LoginModal

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
`

const OTCBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`

const OTCButton = styled(Clickable)`
  white-space: nowrap;
`

const StyledOTCIcon = styled(Icon)<{ $status: string }>`
  margin: 0 16px;
  color: ${(p) => (p.$status === 'success' ? success : error)};
`

const OTCError = styled.span`
  color: ${error};
  font-size: 12px;
`
